import {
  Descendant,
  Editor,
  Node,
  NodeEntry,
  Range,
  Element as SlateElement,
  Text,
  Transforms,
} from 'slate';
import { ReactEditor } from 'slate-react';
import { NodeInsertNodesOptions } from 'slate/dist/interfaces/transforms/node';

import { emojiMap } from './constants';
import { CustomElement, ElementType, Format } from './types';

export const LIST_TYPES = ['numbered-list', 'bulleted-list'] as const;
export type ListType = (typeof LIST_TYPES)[number];

const isElementActive = (editor: Editor, type: ElementType) => {
  const [result] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === type,
  });
  return !!result;
};

const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

const isFormatActive = (editor: Editor, format: Format) => {
  const { selection } = editor;

  if (!selection) return false;

  // If the selection is collapsed, check the marks at the cursor position
  if (Range.isCollapsed(selection)) {
    try {
      const marks = Editor.marks(editor);
      return marks ? marks[format] === true : false;
    } catch (e) {
      return false; // Safe fallback when selection is not on a leaf node
    }
  }

  // Look for text nodes with the format applied within the selection range
  const [match] = Editor.nodes(editor, {
    match: (n) => Text.isText(n) && n[format] === true,
    mode: 'all',
  });

  return !!match;
};

const getRawText = (editor: Editor) => {
  return editor.children.map((node) => Node.string(node)).join('\n');
};

const toggleFormat = (editor: Editor, format: Format) => {
  const isActive = isFormatActive(editor, format);

  if (editor.selection && Range.isCollapsed(editor.selection)) {
    // Apply or remove the mark from the cursor position when no text is selected
    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  } else {
    // Apply or remove the mark to the selected text
    Transforms.setNodes(
      editor,
      { [format]: isActive ? null : true },
      {
        match: (n) => Text.isText(n), // Apply to text nodes only
        split: true,
      },
    );
  }

  ReactEditor.focus(editor);
};

const wrapLink = (editor: Editor, url: string, textLink: string) => {
  if (isElementActive(editor, 'link')) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: CustomElement = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: textLink }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

const insertLink = (editor: Editor, url: string, textLink: string) => {
  if (!editor.selection) return;

  wrapLink(editor, url, textLink);

  const { selection } = editor;
  if (!selection) return;

  const linkEnd = Editor.after(editor, selection.focus, { unit: 'offset' });
  if (linkEnd) {
    Transforms.select(editor, {
      anchor: linkEnd,
      focus: linkEnd,
    });

    ReactEditor.focus(editor);
  }
};

const getSelectedLink = (editor: Editor) => {
  const [linkEntry] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === 'link',
  });
  return linkEntry;
};

function isUrl(text: string) {
  return text.match(/^https?:\/\/[^\s]+$/);
}

const isRichText = (template: string | CustomElement[]): boolean => {
  return Array.isArray(template) && template.every((node) => typeof node === 'object');
};

const insertTemplate = (editor: Editor, template: string | CustomElement[]) => {
  if (typeof template !== 'string') {
    const sanitizedNodes = (template as CustomElement[]).map((node) => {
      return {
        ...node,
        children: node.children.map((child) => ({
          ...child,
          key: undefined,
        })),
      };
    });

    const options: NodeInsertNodesOptions<Node> = {};

    Transforms.insertNodes(editor, sanitizedNodes, options);
    Transforms.collapse(editor, { edge: 'end' });
  } else {
    Transforms.insertText(editor, template);
  }

  ReactEditor.focus(editor);
};

const isImageUrl = (url?: string) => {
  if (!url) return false;
  if (!isUrl(url)) return false;
  const ext = new URL(url).pathname.split('.').pop();

  return ['jpg', 'png', 'webp'].includes(ext || '');
};

const insertImage = (editor: Editor, url: string) => {
  const image: CustomElement = { type: 'image', children: [{ text: url }] };
  Transforms.insertNodes(editor, image);
};

const clearEditor = (editor: Editor, initial: CustomElement[] | Descendant[]) => {
  const point = { path: [0, 0], offset: 0 };
  editor.selection = { anchor: point, focus: point };
  editor.history = { redos: [], undos: [] };
  editor.children = initial;
};

// Focus the editor and drop the caret at the end of its content. Deferred a tick
// so it runs after the current render commit (the editable node must be mounted
// and any newly-loaded value applied). Swallows the "can't resolve DOM node"
// error Slate throws if the editor isn't mounted yet.
const focusEditor = (editor: Editor) => {
  setTimeout(() => {
    try {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.end(editor, []));
    } catch {
      /* editor not mounted — ignore */
    }
  }, 0);
};

const convertFromTextToRichText = (text: string | CustomElement[]) => {
  if (typeof text === 'string') {
    const result = text.split('\n').map((line) => {
      return { type: 'paragraph', children: [{ text: line }] } as CustomElement;
    });

    return result;
  } else {
    return text as CustomElement[];
  }
};

const insertTextAtCursor = (editor: Editor, tag: string) => {
  const tagNode = { text: tag };
  Transforms.insertNodes(editor, tagNode);
  ReactEditor.focus(editor); // Ensure the editor is focused after inserting
};

const insertNodesAtCursor = (editor: Editor, nodes: CustomElement[]) => {
  Transforms.insertNodes(editor, nodes);
  ReactEditor.focus(editor);
};

const handleTextReplacement = (editor: Editor) => {
  const { selection } = editor;

  if (selection && Range.isCollapsed(selection)) {
    const [start] = Range.edges(selection);

    // Iterate over possible distances (3 and 2) to check for patterns
    for (const distance of [2, 3]) {
      const before = Editor.before(editor, start, { unit: 'character', distance });

      if (before) {
        const beforeRange = { anchor: before, focus: start };
        const beforeText = Editor.string(editor, beforeRange);

        // Replace if pattern is found
        const emoji = Object.entries(emojiMap).find(([pattern]) =>
          beforeText.endsWith(pattern),
        )?.[1];

        if (emoji) {
          Transforms.select(editor, beforeRange);
          Transforms.delete(editor);
          Transforms.insertText(editor, emoji);
          return;
        }
      }
    }
  }
};

const tagRegex = /\{\{(.*?)\}\}/g;

const decorate = (entry: NodeEntry) => {
  const [node, path] = entry;
  const ranges = [];
  if (Text.isText(node)) {
    let match;
    const { text } = node;

    // Find matches using regex
    while ((match = tagRegex.exec(text)) !== null) {
      ranges.push({
        anchor: { path, offset: match.index },
        focus: { path, offset: match.index + match[0].length },
        tag: true,
      });
    }
  }
  return ranges as Range[];
};

const isBlockActive = (editor: Editor, format: ElementType) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) => {
        if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
          return n.type === format;
        }
        return false;
      },
    }),
  );

  return !!match;
};

const isListType = (format: ElementType) => {
  return LIST_TYPES.includes(format as ListType);
};

const toggleBlock = (editor: Editor, format: ElementType) => {
  const isActive = isBlockActive(editor, format);
  const isList = isListType(format);

  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && isListType(n.type),
    split: true,
  });

  const newProperties: Partial<SlateElement> = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  };

  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const isEmptyEditorValue = (data: any): boolean => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return true;
  }

  const checkEmptyNode = (node: any): boolean => {
    if (!node) return true;

    if (node.text && node.text.trim() !== '') {
      return false;
    }

    if (node.children && Array.isArray(node.children)) {
      return node.children.every(checkEmptyNode);
    }

    return true;
  };

  return data.every(checkEmptyNode);
};

const richTextToPlainText = (nodes: SlateElement[]): string => {
  return nodes.map((node) => Node.string(node)).join('\n');
};

const mentionToText = (node: any): string => {
  // Generic mention ({ mention: { label } }) or the internal-note shape
  // ({ user: { firstname, lastname } }).
  if (node?.mention?.label) return `@${node.mention.label}`;
  const user = node?.user;
  const name = [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim();
  return name ? `@${name}` : '@user';
};

const nodeToText = (node: any): string => {
  if (!node || typeof node !== 'object') return '';

  if (typeof node.text === 'string') return node.text;

  if (node.type === 'mention') {
    return mentionToText(node);
  }

  if (Array.isArray(node.children)) {
    return node.children.map(nodeToText).join('');
  }

  return '';
};

// Collect the ids of all @-mentions in a Slate value — for the mutation payload
// (e.g. notifying mentioned users). Supports the generic ({ mention: { id } })
// and internal-note ({ user: { _id } }) shapes.
const getMentionIds = (nodes: any[]): string[] => {
  const ids: string[] = [];
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'mention') {
      const id = node.mention?.id ?? node.user?._id;
      if (id) ids.push(id);
    }
    if (Array.isArray(node.children)) node.children.forEach(walk);
  };
  (Array.isArray(nodes) ? nodes : []).forEach(walk);
  return Array.from(new Set(ids));
};

const richTextWithMentionsToText = (nodes: Descendant[]): string => {
  return nodes.map(nodeToText).join('\n');
};

//TODO: Please remove this function
// when we are sure that data from the backend is correct
// this is bad solution to avoid issues with the editor
function sanitizeRichTextData(data: any): CustomElement[] {
  if (!Array.isArray(data)) return [];

  return data
    .map((node) => {
      // Convert {text: 'some text'} to { type: 'paragraph', children: [{ text: 'some text' }] }
      if (typeof node === 'object' && node !== null && typeof node.text === 'string') {
        return {
          type: 'paragraph',
          children: [node],
        };
      } else {
        return node;
      }
    })
    .filter(
      (node) =>
        typeof node === 'object' &&
        node !== null &&
        Array.isArray(node.children) &&
        typeof node.type === 'string',
    );
}

export {
  getRawText,
  toggleFormat,
  insertLink,
  isFormatActive,
  getSelectedLink,
  isElementActive,
  isUrl,
  insertTemplate,
  isRichText,
  insertImage,
  isImageUrl,
  clearEditor,
  focusEditor,
  convertFromTextToRichText,
  insertTextAtCursor,
  insertNodesAtCursor,
  handleTextReplacement,
  wrapLink,
  decorate,
  toggleBlock,
  isBlockActive,
  isEmptyEditorValue,
  isListType,
  richTextToPlainText,
  richTextWithMentionsToText,
  getMentionIds,
  sanitizeRichTextData,
};
